// Public pricing client — GOVERNED, read-only, unauthenticated.
//
// Reads ONLY the existing canonical commercial endpoints (no second pricing API,
// no hard-coded plan/price/limit/feature catalog in the frontend):
//   • GET /api/v1/billing/plans/comparison  — the PUBLISHED-version projection
//     (per-plan included feature keys, the 5 canonical limits, trial_days and
//     monthly price). This is the SAME published-version resolution that
//     signup/checkout uses (resolve_published_plan_version), so the public card
//     and signup stay in parity (Option A).
//   • GET /api/v1/billing/plans            — governed marketing copy: plan
//     description + included_credits (display only).
//
// The removed /api/v1/public/pricing is intentionally NOT used.
//
// SIX-OPTION UI from a FIVE-PLAN API: the API returns the 5 ladder plans
// (Starter…Enterprise). "Trial" is a distinct signup-time offer that is absent
// from the plan-ladder records, so it is presented as its own card WITHOUT any
// invented allowances/entitlements and is NOT added to the entitlement
// comparison (the page filters it out of the matrix + estimator).

const BASE = process.env.REACT_APP_BACKEND_URL || '';

async function getJson(path, signal) {
  const res = await fetch(`${BASE}${path}`, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw new Error(`pricing_unavailable_${res.status}`);
  return res.json();
}

export async function fetchPublicPricing(signal) {
  const [cmp, plansDoc] = await Promise.all([
    getJson('/api/v1/billing/plans/comparison', signal),
    // Secondary (governed copy only). Non-fatal if unavailable — comparison is the source of truth.
    getJson('/api/v1/billing/plans', signal).catch(() => null),
  ]);
  if (!cmp || !Array.isArray(cmp.plans)) throw new Error('pricing_malformed');

  // Authoritative friendly feature names from the server capability catalogue.
  const featureLabels = {};
  (cmp.capability_catalog || []).forEach((f) => { if (f && f.key) featureLabels[f.key] = f.name; });

  // Governed description + included credits, keyed by plan id, from /billing/plans.
  const copyById = {};
  (plansDoc?.plans || []).forEach((p) => { if (p && p.id) copyById[p.id] = p; });

  const ladder = cmp.plans.map((p) => {
    const cents = p.monthly_price_cents;
    return {
      plan_id: p.plan_key,
      display_name: p.display_name,
      price_usd: p.on_request ? null : (cents == null ? null : cents / 100),
      contact_pricing: !!p.on_request,
      trial_days: p.trial_days || null,
      features: Array.isArray(p.included_feature_keys) ? p.included_feature_keys : [],
      limits: p.limits || {},                       // already the 5 canonical limit keys
      description: copyById[p.plan_key]?.description || null,
      included_credits: copyById[p.plan_key]?.included_credits || null,
      price_source: p.price_source,
      is_trial: false,
    };
  });

  // Distinct Trial card — signup-time offer. NO invented allowances/entitlements:
  // it carries no limits/features matrix and is excluded from the comparison.
  const trialDays = ladder.reduce((m, p) => (p.trial_days ? Math.max(m, p.trial_days) : m), 0) || 14;
  const trialCard = {
    plan_id: 'trial',
    display_name: 'Trial',
    price_usd: 0,
    contact_pricing: false,
    trial_days: trialDays,
    features: [],
    limits: {},
    description: null,
    included_credits: null,
    price_source: cmp.source,
    is_trial: true,
  };

  return {
    currency: (cmp.plans[0]?.currency || 'usd').toUpperCase(),
    annual_pricing_available: !!cmp.annual_pricing_available,
    source: cmp.source,
    feature_labels: featureLabels,
    plans: [trialCard, ...ladder],
    // No PUBLIC add-on / usage-pack / meter projection exists (those catalog routes
    // are tenant-scoped / admin-gated). The page surfaces this honestly rather than
    // fabricating a shadow catalog.
    add_ons: [],
    usage_packs: [],
  };
}
